"use client";

import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

interface FAQItemProps {
  question: string;
  answer: string;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-900 transition-colors"
      >
        <span className="font-semibold text-white">{question}</span>
        {isOpen ? (
          <ChevronUp className="text-cyan-400 flex-shrink-0" size={20} />
        ) : (
          <ChevronDown className="text-cyan-400 flex-shrink-0" size={20} />
        )}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 text-gray-300">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

const FAQ: React.FC = () => {
  const faqs = [
    {
      question: "What exactly do I get?",
      answer: "You get the complete source code for a production-ready referral system, including all API routes, Prisma schema, React components, Stripe Connect integration, and admin dashboard. Everything is TypeScript and ready to customize.",
    },
    {
      question: "Can I get a refund?",
      answer: "No. This is digital code delivered instantly. All sales are final. Download the free PDF first to see the complete structure and decide if it's right for you.",
    },
    {
      question: "Is this just a PDF or actual code?",
      answer: "This is the actual source code—a complete Next.js application with all files, components, and database schema. You'll receive a download link via email immediately after purchase.",
    },
    {
      question: "Do I need PeerQuik to use this?",
      answer: "No. This code works standalone. However, it's extracted from PeerQuik and designed to integrate seamlessly if you're using the boilerplate. You can also adapt it to any Next.js project.",
    },
    {
      question: "What tech stack does it use?",
      answer: "Next.js 14 (App Router), TypeScript, Prisma (PostgreSQL), Stripe & Stripe Connect, Tailwind CSS, and React. You'll need basic knowledge of these to customize it for your project.",
    },
    {
      question: "Will you help me implement this?",
      answer: "This is self-service code. The tutorial includes comments and clear structure, but no implementation support is provided. If you need help, consider hiring a developer or checking the PeerQuik community.",
    },
    {
      question: "Can I use this for client projects?",
      answer: "Yes. Once purchased, you can use this code in unlimited projects—commercial or personal. You cannot resell or redistribute the code itself.",
    },
  ];

  return (
    <section className="bg-black text-white py-16 px-4">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center">
          Frequently Asked Questions
        </h2>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <FAQItem key={index} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
